import type { PublishedAppShareIntent } from '@common/api/publishedAppSharingService';

export const PUBLISHED_APP_SHARE_HANDOFF_KEY = 'bustly:published-app-share-handoff';

export interface PublishedAppShareHandoff {
    intent?: string;
    hostname: string;
    displayName?: string;
    expiresAt: number;
}

export const readInviteTokenFromFragment = (hash: string): string | null => {
    if (!hash.startsWith('#')) return null;
    const token = new URLSearchParams(hash.slice(1)).get('t')?.trim() || '';
    return token.length >= 32 && token.length <= 512 ? token : null;
};

export const clearInviteFragment = (location: Location, history: History) => {
    history.replaceState(history.state, '', `${location.pathname}${location.search}`);
};

export const saveIntentHandoff = (
    intent: PublishedAppShareIntent,
    storage: Pick<Storage, 'setItem'>,
    now = Date.now(),
) => {
    const handoff: PublishedAppShareHandoff = {
        intent: intent.intent,
        hostname: intent.hostname,
        displayName: intent.displayName,
        expiresAt: now + intent.expiresInSeconds * 1_000,
    };
    storage.setItem(PUBLISHED_APP_SHARE_HANDOFF_KEY, JSON.stringify(handoff));
    return handoff;
};

export const exchangeInviteTokenForHandoff = async (
    rawToken: string,
    createIntent: (token: string) => Promise<PublishedAppShareIntent>,
    storage: Pick<Storage, 'setItem'>,
    location: Location,
    history: History,
    now = Date.now(),
) => {
    const intent = await createIntent(rawToken);
    const handoff = saveIntentHandoff(intent, storage, now);
    clearInviteFragment(location, history);
    return handoff;
};

export const saveHostnameHandoff = (
    hostname: string,
    storage: Pick<Storage, 'setItem'>,
    now = Date.now(),
) => {
    const handoff: PublishedAppShareHandoff = {
        hostname,
        expiresAt: now + 15 * 60 * 1_000,
    };
    storage.setItem(PUBLISHED_APP_SHARE_HANDOFF_KEY, JSON.stringify(handoff));
    return handoff;
};

export const readShareHandoff = (
    storage: Pick<Storage, 'getItem' | 'removeItem'>,
    now = Date.now(),
): PublishedAppShareHandoff | null => {
    const raw = storage.getItem(PUBLISHED_APP_SHARE_HANDOFF_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as Partial<PublishedAppShareHandoff>;
        if (
            typeof parsed.hostname !== 'string'
            || !parsed.hostname.trim()
            || typeof parsed.expiresAt !== 'number'
            || parsed.expiresAt <= now
            || (parsed.intent !== undefined && typeof parsed.intent !== 'string')
        ) {
            storage.removeItem(PUBLISHED_APP_SHARE_HANDOFF_KEY);
            return null;
        }
        return parsed as PublishedAppShareHandoff;
    } catch {
        storage.removeItem(PUBLISHED_APP_SHARE_HANDOFF_KEY);
        return null;
    }
};

export const isPublishedAppShareRedirect = (
    search: string,
    storage: Pick<Storage, 'getItem' | 'removeItem'>,
    allowedOrigin = 'https://www.bustly.ai',
) => {
    const encodedRedirect = new URLSearchParams(search).get('redirect');
    const handoff = readShareHandoff(storage);
    if (!encodedRedirect || !handoff?.intent) return false;
    try {
        const origin = new URL(allowedOrigin).origin;
        const target = new URL(decodeURIComponent(encodedRedirect), origin);
        return target.origin === origin && target.pathname === '/apps/share';
    } catch {
        return false;
    }
};
